/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import { Image } from '@msdyn365-commerce/core';
import { ICommerceApiSettings, ICoreContext, IGeneric, IAny } from '@msdyn365-commerce/core-internal';
import { generateSwatchImageUrl, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IBuyboxCommonResources, DropdownIds } from '../buyboxInterface';
import { EmailComponent } from './email-handler';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
/**
 * Swatch item data.
 */
export interface ISwatchItem {
    value: string;
    itemId: string;
    dimensionType: DropdownIds;
    colorHexCode?: string;
    imageUrl?: string;
    productImageUrls?: string[];
    isDisabled?: boolean;
    isDefault?: boolean;
    isValid?: boolean;
    swatchItemAriaLabel?: string;
}

/**
 * Swatch item configuration.
 */
export interface ISwatchItemProps {
    className: string;
    apiSettings: ICommerceApiSettings;
    requestContext: ICoreContext<IGeneric<IAny>>;
    resources: IBuyboxCommonResources;
    isSelected: boolean;
    isSelectionEnabled?: boolean;

    item: ISwatchItem;
    isRefineItem?: boolean;

    onSelectItem(item: ISwatchItem): void;

    swatchProducts: SimpleProduct[];

    triggerOOSEmailHandler(email: string): Promise<boolean>;

    triggerOOSModal: () => string;
    context: ICoreContext;
}

/**
 * Represents a single item for swatch.
 * @param props - Item configuration.
 * @returns React node.
 */
const swatchImage = (props: ISwatchItemProps, swatchImageUrl: string | undefined): JSX.Element => {
    const { isSelected, className, item, context } = props;
    const { itemId } = item;
    return (
        <Image
            id={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
            src={swatchImageUrl}
            altText=''
            className={classnames(
                className,
                `${className}-${itemId}`,
                `${className}__image`,
                isSelected ? `${className}__selected` : `${className}__unselected`,
                item.isDisabled ? `${className}__disabled` : undefined
            )}
            gridSettings={context.request.gridSettings!}
            imageFallbackOptimize={true}
            bypassHideOnFailure={false}
            loadFailureBehavior='empty'
            role='tabpanel'
        />
    );
};
export const SwatchItemComponent: React.FC<ISwatchItemProps> = (props: ISwatchItemProps) => {
    const { isSelected, className, onSelectItem, item, context } = props;
    const { itemId, value, colorHexCode, imageUrl, swatchItemAriaLabel, dimensionType } = item;

    const style: React.CSSProperties = {};

    let isColored = false;

    const hasColor = !StringExtensions.isNullOrWhitespace(colorHexCode);
    const hasImage = !StringExtensions.isNullOrWhitespace(imageUrl);

    const selectItem = React.useCallback(() => {
        onSelectItem(item);
    }, [onSelectItem, item]);

    const selectionHandler = props.isSelectionEnabled ? selectItem : undefined;

    if (hasColor) {
        style.backgroundColor = ''; // colorHexCode;
        isColored = true;
    } else if (hasImage) {
        const fullImageUrl = generateSwatchImageUrl(imageUrl!, props.apiSettings);
        return (
            <Image
                id={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                src={fullImageUrl}
                className={classnames(
                    className,
                    `${className}-${itemId}`,
                    `${className}__image`,
                    isSelected ? `${className}__selected` : `${className}__unselected`,
                    item.isDisabled ? `${className}__disabled` : undefined
                )}
                style={style}
                onClick={selectionHandler}
                onKeyPress={selectionHandler}
                title={value}
                aria-label={value}
                alt={value}
                gridSettings={context.request.gridSettings!}
                imageFallbackOptimize={true}
                role='tabpanel'
            />
        );
    }

    // VSI Customization to resolve broken image issue
    const [swatchImageUrl, setSwatchImageUrl] = React.useState('');

    if (item.dimensionType === DropdownIds.color) {
        const { swatchProducts } = props;
        const colorImages = swatchProducts.find(simpleProduct => {
            return simpleProduct.Dimensions![1].DimensionValue?.Value === value;
        });
        const swatchSize = (colorImages?.Dimensions && colorImages.Dimensions[0].DimensionValue?.Value)?.replace('/', '^');
        const swatchColor = value?.replace('/', '^');
        const swatchImage = `Products/${colorImages?.ItemId} ^  ^ ${swatchSize} ^ ${swatchColor} ^ None_000_000_swatch.jpg`;
        const fullImageUrl = `${generateSwatchImageUrl(swatchImage, props.apiSettings)}&w=28&h=37&q=80&m=6`;
        if (fullImageUrl) {
            React.useEffect(() => {
                setSwatchImageUrl(fullImageUrl);
            }, [fullImageUrl]);
        }
    }

    const shouldDisplayText = !isColored;
    const sizeDimension = item.dimensionType === DropdownIds.size;
    const colorDimension = item.dimensionType === DropdownIds.color;

    return (
        <div className={classnames(isSelected ? `${className}__selected` : `${className}__unselected`)}>
            <button
                key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                className={classnames(
                    className,
                    `${className}-${itemId}`,
                    isSelected ? `${className}__selected` : `${className}__unselected`,
                    isColored ? `${className}__colored` : `${className}__uncolored`,
                    !item.isValid ? `${className}__master` : item.isDisabled ? `${className}__disabled ooStock-button` : undefined
                )}
                style={style}
                onClick={selectionHandler}
                onKeyPress={selectionHandler}
                title={value}
                {...(swatchItemAriaLabel ? { 'aria-label': format(swatchItemAriaLabel, dimensionType) } : null)}
                {...(props.isRefineItem ? { tabIndex: -1 } : null)}
                disabled={!props.isSelectionEnabled}
            >
                {sizeDimension && shouldDisplayText && value}
                {colorDimension && swatchImageUrl !== '' && swatchImage(props, swatchImageUrl)}
            </button>
            {/*  VSI Customization to show Email Icon for OOS size in selected Varients */}
            {item.isValid && isSelected && item.isDisabled && sizeDimension && (
                <div className='mail-wrapper'>
                    <EmailComponent {...props} isColored={isColored} />
                </div>
            )}
        </div>
    );
};
